import { AccessControlled } from '@/components/access-control'
import { TabCountBadge } from '@/components/common/TabCountBadge'
import {
	usePageHeaderActions,
	usePageHeaderBackButton,
	usePageHeaderCustomViews,
	usePageHeaderTabs,
} from '@/components/page-context/HeaderActionsHook'
import { usePageContext } from '@/components/page-context/context'
import type { BackButtonConfig, ButtonPageAction, PageAction, PageTab } from '@/components/page-context/types'
import { Spinner } from '@/components/ui/spinner'
import { useAccessControl } from '@/hooks/access-control/useAccessControl'
import { useIsMobile } from '@/hooks/use-mobile'
import { usePageTabCounts } from '@/hooks/usePageTabCounts'
import { useQuotaUsageTooltip } from '@/hooks/useQuotaUsageTooltip'
import { getRouteInfo, isOrganizationLevelRoute, normalizePath, useRouteContext } from '@/lib/routerUtils'
import { cn } from '@/lib/utils'
import { useDeviceFilterStore } from '@/stores/deviceFilterStore'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Link, type LinkProps, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronLeft, GripVertical, PlusIcon } from 'lucide-react'
import * as React from 'react'
// import { flushSync } from 'react-dom'
import { toast } from 'sonner'
import {
	type NavItem,
	navData,
	organizationNavData,
	organizationSettingsNavData,
	settingsNavData,
} from '../data/nav-data'
import { CustomViewCreator } from './Views/CustomViewCreator'
import { CustomViewTabs } from './Views/CustomViewTabs'
import { useCustomViewsContext } from './Views/CustomViewsContext'
import { CustomViewsDropdown, getFilteredLengthForView } from './Views/CustomViewsDropdown'
import type { CustomView } from './Views/useCustomViews'
import { useCustomViews } from './Views/useCustomViews'
import type { FilterOption } from './common-table/FilterSearchBar'

// Custom view creation (the "+" tab button) is disabled site-wide.
// Flip to true to re-enable.
const ENABLE_VIEW_CREATION = false

function shouldShowTabCountBadge(tab: { showCount?: boolean }): boolean {
	return tab.showCount !== false
}

function getTabCountForDropdown(tab: { showCount?: boolean; count?: number }): number | null {
	if (tab.showCount === false) {
		return null
	}
	return tab.count ?? null
}
import { Button, buttonVariants } from './ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { Separator } from './ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

// Regular tab button component
interface TabButtonProps {
	tab: PageTab
}

export function TabButton({ tab }: TabButtonProps) {
	return (
		<button
			type='button'
			key={tab.key}
			onClick={tab.onClick}
			className={cn(
				'group inline-flex h-control items-center gap-1.5 px-3 font-medium rounded-[6px] whitespace-nowrap transition-colors duration-300 ease-in-out cursor-pointer text-body!',
				tab.isActive
					? 'text-header-tab-active-text bg-header-tab-active-bg hover:bg-header-tab-active-bg-hover'
					: 'bg-header-tab-inactive-bg text-header-tab-inactive-text group-hover:text-header-tab-active-text',
			)}>
			<span
				className={cn(
					tab.isActive
						? 'text-header-tab-active-text'
						: 'text-header-tab-inactive-text group-hover:text-header-tab-active-text',
				)}>
				{tab.name}
			</span>
			<TabCountBadge count={tab.count} active={tab.isActive} showCount={shouldShowTabCountBadge(tab)} />
		</button>
	)
}

// Sortable tab item component
interface SortableTabItemProps {
	tab: PageTab
	isActive: boolean
}

export function SortableTabItem({ tab, isActive }: SortableTabItemProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
		id: tab.key,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				'group h-(--height-control) rounded-[6px] cursor-pointer text-body!',
				isActive
					? 'bg-header-tab-active-bg text-header-tab-active-text hover:bg-header-tab-active-bg-hover'
					: 'bg-header-tab-inactive-bg text-header-tab-inactive-text group-hover:text-header-tab-active-text',
			)}>
			<div
				ref={setActivatorNodeRef}
				{...attributes}
				{...listeners}
				className={cn(
					'flex items-center justify-between flex-1 gap-2 cursor-grab active:cursor-grabbing size-full px-2',
					isActive
						? 'text-header-tab-active-text bg-header-tab-active-bg hover:bg-header-tab-active-bg-hover'
						: 'text-header-tab-inactive-text group-hover:text-header-tab-active-text',
				)}
				onClick={tab.onClick}>
				<GripVertical className='size-(--icon-standalone) text-muted-foreground shrink-0' />
				<span className='font-normal truncate text-body'>{tab.name}</span>
				<TabCountBadge count={tab.count} active={isActive} showCount={shouldShowTabCountBadge(tab)} />
			</div>
		</div>
	)
}

function getTabsForPath(navData: NavItem[], path: string): NavItem[] {
	// Dashboards tabs (All, Device, Fleet) should stay on the dashboards page only and not move to child pages
	if (
		path.startsWith('/data/dashboards') &&
		!path.startsWith('/data/dashboards/all') &&
		!path.startsWith('/data/dashboards/device') &&
		!path.startsWith('/data/dashboards/fleet')
	) {
		return []
	}

	if (path.startsWith('/settings')) {
		const allSettingsNavData = [...settingsNavData, ...organizationSettingsNavData]
		for (const item of allSettingsNavData) {
			if (item.path) {
				const basePath = item.path.includes('/all')
					? item.path.replace('/all', '')
					: item.path.includes('/active')
						? item.path.replace('/active', '')
						: item.path
				if (path === basePath || path.startsWith(`${basePath}/`)) {
					return item.items || []
				}
			}
		}
	}

	for (const section of navData) {
		if (section.settingsOptions) {
			for (const settingsOption of section.settingsOptions) {
				if (
					settingsOption.path &&
					(path.startsWith(settingsOption.path) ||
						(settingsOption.path.includes('/all') && path.startsWith(settingsOption.path.replace('/all', ''))))
				) {
					return settingsOption.items || []
				}
			}
		}
	}

	const findMatchingTabs = (items: NavItem[]): NavItem[] => {
		for (const item of items) {
			if (item.path) {
				const basePath = item.path.substring(0, item.path.lastIndexOf('/'))
				if (basePath && path.startsWith(basePath)) {
					if (item.items && item.items.length > 0) {
						return item.items
					}
				}
			}
			if (item.items && item.items.length > 0) {
				const found = findMatchingTabs(item.items)
				if (found.length > 0) {
					return found
				}
			}
		}
		return []
	}

	return findMatchingTabs(navData)
}

function useTabs(navData: NavItem[]) {
	const { pathname } = useRouteContext()

	return React.useMemo(() => {
		const normalized = normalizePath(pathname)
		return getTabsForPath(navData, normalized)
	}, [navData, pathname])
}

// Hook to get current search params for preserving them in navigation
function useCurrentSearch() {
	const { search } = useRouteContext()
	return search
}

function RouterTab({
	to,
	params,
	search,
	active,
	count,
	showCount,
	children,
	onClick,
	tabRef,
}: LinkProps & {
	active?: boolean
	count?: number
	showCount?: boolean
	children: React.ReactNode
	onClick?: () => void
	tabRef?: React.Ref<HTMLElement>
}) {
	return (
		<span
			ref={tabRef}
			onClick={onClick}
			onKeyDown={event => {
				if (event.key === 'Enter' || event.key === ' ') {
					onClick?.()
				}
			}}
			style={{ display: 'inline-flex' }}>
			<Link
				to={to}
				params={params}
				search={search}
				className={cn(
					'group inline-flex h-control items-center gap-1.5 px-3 text-body font-medium rounded-[6px] whitespace-nowrap cursor-pointer',
					'relative z-10 transition-colors duration-300 ease-in-out',
					active
						? 'text-header-tab-active-text bg-header-tab-active-bg hover:bg-header-tab-active-bg-hover'
						: 'bg-header-tab-inactive-bg text-header-tab-inactive-text hover:text-header-tab-active-text',
				)}>
				<span className='text-body'>{children}</span>
				<TabCountBadge count={count} active={active} showCount={showCount !== false} />
			</Link>
		</span>
	)
}

function getResourceForPath(path: string): string | undefined {
	const allNavItems = [...settingsNavData, ...organizationSettingsNavData, ...navData]
	for (const item of allNavItems) {
		if (item.path) {
			const basePath = item.path.replace(/\/(all|active|inactive)$/, '')
			if (path.startsWith(basePath)) {
				return item.resource
			}
		}
		if (item.items) {
			for (const child of item.items) {
				if (child.path) {
					const childBasePath = child.path.replace(/\/(all|active|inactive)$/, '')
					if (path.startsWith(childBasePath)) {
						return item.resource || child.resource
					}
				}
			}
		}
	}
	return undefined
}

const MAX_VISIBLE_TABS = 3 // All tab + 2 other tabs

function HeaderBackAndTitleControls({
	backButton,
	titleActions,
	backButtonClassName,
}: {
	backButton: BackButtonConfig | null | undefined
	titleActions: PageAction[]
	backButtonClassName?: string
}) {
	const navigate = useNavigate()
	return (
		<>
			{backButton && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant='ghost'
							size='icon'
							className={cn('size-control mr-2 shrink-0', backButtonClassName)}
							onClick={() => {
								navigate({
									to: backButton.to,
									params: backButton.params,
								})
							}}>
							<ChevronLeft className='size-(--icon-standalone)' />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{backButton.tooltip || 'Go back'}</TooltipContent>
				</Tooltip>
			)}
			{titleActions.length > 0 && (
				<div className='flex items-center gap-2 mr-2 shrink-0'>
					{titleActions.map(action => {
						if (action.type !== 'component') {
							return null
						}
						return <div key={action.id}>{action.component}</div>
					})}
				</div>
			)}
		</>
	)
}

interface PageHeaderButtonActionProps {
	action: ButtonPageAction
	isMobile: boolean
}

function PageHeaderButtonAction({ action, isMobile }: PageHeaderButtonActionProps) {
	const access = useAccessControl(action.access ?? {})
	const tooltip = useQuotaUsageTooltip(action.tooltip, action.access)

	const isIconOnly = !action.label || action.label.trim() === ''
	const isMobileIconOnly = isMobile && action.icon
	const effectiveIsIconOnly = isIconOnly || isMobileIconOnly
	const hasChildren = action.children && action.children.length > 0

	const primaryButton = (
		<Button
			key={action.id}
			variant={action.variant || 'default'}
			size={effectiveIsIconOnly ? 'icon' : 'sm'}
			onClick={action.onClick}
			disabled={action.disabled || action.loading}
			data-testid={action.dataTestId}
			className={cn(
				'transition-all duration-200',
				effectiveIsIconOnly ? 'size-(--height-control)' : 'h-(--height-control)',
				hasChildren && 'control-base-split-r',
			)}>
			{action.loading && <Spinner className='mr-1' />}
			{action.icon && !action.loading && <span className='shrink-0'>{action.icon}</span>}
			{!effectiveIsIconOnly && <span className='whitespace-nowrap text-control'>{action.label}</span>}
		</Button>
	)

	let childActions: React.ReactNode | null = null
	if (hasChildren) {
		childActions = (
			<DropdownMenu>
				<DropdownMenuTrigger
					asChild
					className='rounded-l-none border-l-0 bg-primary control-base control-base-split-l text-primary-foreground hover:bg-primary/90 px-0'>
					<Button
						aria-label={`${action.label} options`}
						className={cn(
							buttonVariants({
								variant: action.variant || 'default',
								size: 'sm',
							}),
							'rounded-l-none px-2 border-l-0 h-control',
						)}>
						<ChevronDown className='size-(--icon-standalone)' />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='bottom' align='end' sideOffset={0} className='min-w-40 rounded-t-none mt-1'>
					{action?.children?.map(child => (
						<DropdownMenuItem
							key={child.id}
							disabled={child.disabled || action.loading}
							className='text-body'
							onSelect={event => {
								event.preventDefault()
								if (!child.disabled) {
									child.onClick()
								}
							}}>
							{action.loading && <Spinner className='mr-1' />}
							{child.icon && !action.loading && <span className='shrink-0'>{child.icon}</span>}
							<span>{child.label}</span>
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	const renderedPrimaryButton =
		tooltip || action.disabled ? (
			<Tooltip key={action.id}>
				<TooltipTrigger asChild>{primaryButton}</TooltipTrigger>
				<TooltipContent side='bottom' sideOffset={8} data-testid={`${action.id}-tooltip`}>
					<p>{tooltip || (action.disabled ? 'Action not available' : '')}</p>
				</TooltipContent>
			</Tooltip>
		) : (
			primaryButton
		)

	// 1. If warning state: warning tooltip is ONLY on the primary button. Chevron/dropdown options remain active and no tooltip.
	if (action.access && access.state === 'warning') {
		const wrappedPrimaryButton = (
			<AccessControlled key={action.id} {...action.access}>
				{renderedPrimaryButton}
			</AccessControlled>
		)

		return (
			<div key={action.id} className={cn('inline-flex items-stretch overflow-hidden')}>
				{wrappedPrimaryButton}
				{hasChildren && <Separator orientation='vertical' />}
				{childActions}
			</div>
		)
	}

	// 2. If blocked/denied or any other state: wrap the entire button group in AccessControlled (so entire group is disabled/restricted).
	const buttonWithChildren = (
		<div key={action.id} className={cn('inline-flex items-stretch overflow-hidden')}>
			{renderedPrimaryButton}
			{hasChildren && <Separator orientation='vertical' />}
			{childActions}
		</div>
	)

	if (action.access) {
		return (
			<AccessControlled key={action.id} {...action.access}>
				{buttonWithChildren}
			</AccessControlled>
		)
	}

	return buttonWithChildren
}

export function PageHeader() {
	const { pathname: currentPath, tenantId, orgId } = useRouteContext()
	const isMobile = useIsMobile()
	const navigate = useNavigate()
	const { clearFilters } = useDeviceFilterStore()
	const currentSearch = useCurrentSearch()
	const tabSearch = React.useMemo(() => {
		if (!currentSearch || typeof currentSearch !== 'object') {
			return undefined
		}
		const { filters, ...rest } = currentSearch as Record<string, unknown>
		return Object.keys(rest).length > 0 ? rest : undefined
	}, [currentSearch])
	// Normalize path by removing /orgs/$orgId/projects/$tenantId or /orgs/$orgId prefix
	const normalizedPath = normalizePath(currentPath)
	const dropdownRef = React.useRef<HTMLSpanElement | null>(null)
	const { actions: rawActions } = usePageHeaderActions()
	const { tabs: dynamicTabs } = usePageHeaderTabs()
	const { setHeaderControlsSlot } = usePageContext()

	// custom views
	const { hideCustomViews } = usePageHeaderCustomViews()
	const { backButton } = usePageHeaderBackButton()
	const isOrgLevel = isOrganizationLevelRoute(currentPath)
	const tabsToDisplay = useTabs(isOrgLevel ? organizationNavData : navData)
	const { tabCounts } = usePageTabCounts()
	const activeResource = React.useMemo(() => getResourceForPath(normalizedPath), [normalizedPath])
	const accessState = useAccessControl({
		resource: activeResource as Parameters<typeof useAccessControl>[0]['resource'],
		action: 'view',
	})
	const isAccessRestricted = activeResource !== undefined && accessState.state === 'denied'
	const [activeIndex, setActiveIndex] = React.useState<number>(0)
	const prevIndexRef = React.useRef(0)
	const activeIndexRef = React.useRef(activeIndex)
	const tabRefs = React.useRef<(HTMLElement | null)[]>([])
	const [indicatorStyle, setIndicatorStyle] = React.useState({ left: 0, width: 0 })
	const [dynamicTabsIndicatorStyle, setDynamicTabsIndicatorStyle] = React.useState({ left: 0, width: 0 })
	const dynamicTabsContainerRef = React.useRef<HTMLDivElement>(null)
	const dynamicTabsRefs = React.useRef<(HTMLElement | null)[]>([])
	const containerRef = React.useRef<HTMLDivElement>(null)
	const [isInitializing, setIsInitializing] = React.useState(true)
	const [transitionFrom, setTransitionFrom] = React.useState<{ left: number; width: number } | null>(null)
	const [dynamicTabsActiveIndex, setDynamicTabsActiveIndex] = React.useState(0)
	const [editingViewName, setEditingViewName] = React.useState<string>('')
	const {
		config,
		isViewCreatorOpen,
		setIsViewCreatorOpen,
		currentFilters,
		setCurrentFilters,
		editingViewId,
		setEditingViewId,
		clearRegularFilters,
	} = useCustomViewsContext()

	const { customViews, activeViewId, createView, deleteView, updateView, applyView, reorderViews, clearActiveView } =
		useCustomViews(config?.pageKey || '')

	// --- Tab Organization & Active State Logic ---
	const usingOverflowTabs = config?.overflowTabs !== undefined
	const visibleRouterTabsCount = usingOverflowTabs ? 0 : Math.min(tabsToDisplay.length, MAX_VISIBLE_TABS)
	const availableSlotsForCustomViews = Math.max(0, MAX_VISIBLE_TABS - visibleRouterTabsCount)
	const overflowActionTabs = React.useMemo(() => config?.overflowTabs ?? [], [config?.overflowTabs])

	const visibleOverflowTabs = React.useMemo(
		() => overflowActionTabs.slice(0, availableSlotsForCustomViews),
		[overflowActionTabs, availableSlotsForCustomViews],
	)
	const remainingSlotsAfterOverflow = availableSlotsForCustomViews - visibleOverflowTabs.length
	const visibleCustomViews = React.useMemo(
		() => (remainingSlotsAfterOverflow > 0 ? customViews.slice(0, remainingSlotsAfterOverflow) : []),
		[remainingSlotsAfterOverflow, customViews],
	)

	const effectiveBaseIndex = usingOverflowTabs ? visibleOverflowTabs.length : visibleRouterTabsCount
	const dropdownIndex = effectiveBaseIndex + visibleCustomViews.length

	// Calculate logically active router tab index from URL
	const logicalActiveRouterIndex = tabsToDisplay.findIndex(tab => {
		if (!tab.path) {
			return false
		}
		const tabPath = tab.path.replace(/^\//, '')
		const currentNormalizedPath = normalizedPath.replace(/^\//, '')
		return currentNormalizedPath === tabPath || currentNormalizedPath.startsWith(`${tabPath}/`)
	})

	// Active state detection (independent of visual activeIndex)
	const activeOverflowActionTab = overflowActionTabs.find(tab => tab.isActive)
	const isActiveOverflowTabInVisible = visibleOverflowTabs.some(tab => tab.isActive)
	const activeViewInVisible = visibleCustomViews.some(view => view.id === activeViewId)
	const isActiveRouterTabInOverflow = logicalActiveRouterIndex >= MAX_VISIBLE_TABS && !usingOverflowTabs

	const isSomethingInDropdownActive =
		(activeViewId && !activeViewInVisible) ||
		(activeOverflowActionTab && !isActiveOverflowTabInVisible) ||
		isActiveRouterTabInOverflow

	// useLayoutEffect ensures DOM measurements happen synchronously before paint
	// This prevents visual flickering/overlapping of the sliding indicator
	// biome-ignore lint/correctness/useExhaustiveDependencies: tabsToDisplay, customViews, activeViewId, backButton trigger recalculation when tab DOM structure changes
	React.useLayoutEffect(() => {
		activeIndexRef.current = activeIndex
		const activeTabElement = tabRefs.current[activeIndex]
		const container = containerRef.current

		// Combine checks into a single condition to ensure dependencies are "used" while verifying measurement readiness
		if (!isViewCreatorOpen && activeTabElement && container && activeIndex >= 0 && (config?.data?.length ?? 0) >= 0) {
			const containerRect = container.getBoundingClientRect()
			const tabRect = activeTabElement.getBoundingClientRect()

			const targetStyle = {
				left: tabRect.left - containerRect.left,
				width: tabRect.width,
			}

			if (transitionFrom) {
				setIndicatorStyle(transitionFrom)
				setTransitionFrom(null)

				setTimeout(() => {
					setIndicatorStyle(targetStyle)
				}, 10)
			} else {
				setIndicatorStyle(targetStyle)
			}
		} else {
			// If refs aren't ready yet, schedule a recalculation on next frame
			// This ensures the indicator is positioned correctly on first render
			if ((tabsToDisplay.length > 0 || customViews.length > 0) && activeIndex >= 0) {
				const scheduledActiveIndex = activeIndex
				requestAnimationFrame(() => {
					// Skip if activeIndex changed since we scheduled (e.g. URL effect ran and set correct tab)
					if (activeIndexRef.current !== scheduledActiveIndex) {
						return
					}
					const retryActiveTabElement = tabRefs.current[scheduledActiveIndex]
					const retryContainer = containerRef.current
					if (retryActiveTabElement && retryContainer && scheduledActiveIndex >= 0) {
						const containerRect = retryContainer.getBoundingClientRect()
						const tabRect = retryActiveTabElement.getBoundingClientRect()
						const rafStyle = { left: tabRect.left - containerRect.left, width: tabRect.width }
						setIndicatorStyle(rafStyle)
					}
				})
			}
		}
	}, [
		activeIndex,
		tabsToDisplay,
		customViews,
		activeViewId,
		transitionFrom,
		backButton,
		isViewCreatorOpen,
		config?.data,
	])

	// Close view creator when navigating to a different page
	// biome-ignore lint/correctness/useExhaustiveDependencies: React to path changes
	React.useEffect(() => {
		setIsViewCreatorOpen(false)
		setEditingViewName('')
		clearActiveView()
	}, [currentPath, setIsViewCreatorOpen, setEditingViewName, clearActiveView])

	// Update active index and indicator position for dynamic tabs
	React.useLayoutEffect(() => {
		if (!dynamicTabs?.visibleTabs) {
			return
		}

		const foundIndex = dynamicTabs.visibleTabs.findIndex(tab => tab.isActive)

		// Determine if focus should be on the dropdown (custom view or overflow tab active)
		const baseVisibleTabs = dynamicTabs.visibleTabs.slice(0, MAX_VISIBLE_TABS)
		const activeOverflowTab = dynamicTabs.allTabs?.find(
			tab => tab.isActive && !baseVisibleTabs.some(vt => vt.key === tab.key),
		)
		const isDropdownActive = !!activeViewId || !!activeOverflowTab

		const activeIndex = isDropdownActive
			? baseVisibleTabs.length
			: foundIndex === -1 || foundIndex >= MAX_VISIBLE_TABS
				? 0
				: foundIndex

		// Update active index state if it changed
		if (activeIndex !== dynamicTabsActiveIndex) {
			setDynamicTabsActiveIndex(activeIndex)
		}

		// Calculate and update indicator position synchronously
		const activeTabElement = dynamicTabsRefs.current[activeIndex]
		const container = dynamicTabsContainerRef.current

		// Combine checks into a single condition to ensure dependencies are "used" while verifying measurement readiness
		if (!isViewCreatorOpen && activeTabElement && container && activeIndex >= 0 && (config?.data?.length ?? 0) >= 0) {
			const containerRect = container.getBoundingClientRect()
			const tabRect = activeTabElement.getBoundingClientRect()

			const targetStyle = {
				left: tabRect.left - containerRect.left,
				width: tabRect.width,
			}

			setDynamicTabsIndicatorStyle(targetStyle)
		} else {
			// If refs aren't ready yet, schedule a recalculation on next frame
			if (dynamicTabs?.visibleTabs && dynamicTabs.visibleTabs.length > 0) {
				requestAnimationFrame(() => {
					const retryActiveTabElement = dynamicTabsRefs.current[activeIndex]
					const retryContainer = dynamicTabsContainerRef.current
					if (retryActiveTabElement && retryContainer && activeIndex >= 0) {
						const containerRect = retryContainer.getBoundingClientRect()
						const tabRect = retryActiveTabElement.getBoundingClientRect()
						setDynamicTabsIndicatorStyle({
							left: tabRect.left - containerRect.left,
							width: tabRect.width,
						})
					}
				})
			}
		}
	}, [
		dynamicTabs?.visibleTabs,
		dynamicTabs?.allTabs,
		activeViewId,
		dynamicTabsActiveIndex,
		isViewCreatorOpen,
		config?.data,
	])

	React.useEffect(() => {
		if (config?.pageKey) {
			setIsInitializing(false)
			return
		}
		// Pages that only set actions/tabs (no custom views) never get config; allow header to show
		const hasHeaderContent =
			(rawActions?.length ?? 0) > 0 || !!dynamicTabs || (tabsToDisplay?.length ?? 0) > 0 || !!backButton
		if (hasHeaderContent) {
			setIsInitializing(false)
		}
	}, [config?.pageKey, rawActions?.length, dynamicTabs, tabsToDisplay?.length, backButton])

	// Track previous pageKey to detect navigation to different sections
	const prevPageKeyRef = React.useRef(config?.pageKey)

	// Set activeIndex based on current path when navigating to a different section/page
	// useLayoutEffect ensures activeIndex is set synchronously before paint,
	// preventing visual flickering of the indicator on URL changes
	React.useLayoutEffect(() => {
		if (config?.pageKey && tabsToDisplay.length > 0) {
			// Check if we're navigating to a different page/section
			const isPageChange = prevPageKeyRef.current && prevPageKeyRef.current !== config.pageKey

			if (isPageChange) {
				setActiveIndex(0)
				prevIndexRef.current = 0
				prevPageKeyRef.current = config.pageKey
			} else {
				// 1. If something in the dropdown is active (custom view, overflow action tab, or overflow router tab), pin focus to the dropdown button.
				const activeOverflowTab = overflowActionTabs.find(tab => tab.isActive)
				const isActiveActionTabInDropdown =
					activeOverflowTab && !visibleOverflowTabs.some(v => v.key === activeOverflowTab.key)
				const isActiveViewInDropdown = activeViewId && !visibleCustomViews.some(v => v.id === activeViewId)
				const isActiveRouterTabInDropdown = logicalActiveRouterIndex >= MAX_VISIBLE_TABS && !usingOverflowTabs

				if (isActiveViewInDropdown || isActiveActionTabInDropdown || isActiveRouterTabInDropdown) {
					setActiveIndex(dropdownIndex)
					return
				}

				// 1.5. If a visible custom view is active, focus its tab
				const visibleViewIndex = visibleCustomViews.findIndex(v => String(v.id) === String(activeViewId))
				if (visibleViewIndex !== -1) {
					setActiveIndex(effectiveBaseIndex + visibleViewIndex)
					return
				}

				// 1.6. If a visible overflow action tab is active, focus its tab
				const visibleActionTabIndex = visibleOverflowTabs.findIndex(t => t.isActive)
				if (visibleActionTabIndex !== -1) {
					setActiveIndex(visibleActionTabIndex)
					return
				}

				// Ensure dependencies are "used" for trigger purposes while determining the active index
				const layoutReady = !isViewCreatorOpen && (config?.data?.length ?? 0) >= 0

				// 2. Otherwise, use matching index from URL path (calculated via logicalActiveRouterIndex)
				if (logicalActiveRouterIndex >= 0 && layoutReady) {
					setActiveIndex(logicalActiveRouterIndex)
					prevIndexRef.current = logicalActiveRouterIndex
				} else {
					setActiveIndex(0)
					prevIndexRef.current = 0
				}
			}
		}
	}, [
		config?.pageKey,
		tabsToDisplay,
		activeViewId,
		overflowActionTabs,
		dropdownIndex,
		logicalActiveRouterIndex,
		visibleCustomViews,
		visibleOverflowTabs,
		usingOverflowTabs,
		effectiveBaseIndex,
		isViewCreatorOpen,
		config?.data,
	])

	// Clear edit mode when navigating to a different page/section (not just tab changes)
	React.useEffect(() => {
		if (prevPageKeyRef.current && prevPageKeyRef.current !== config?.pageKey) {
			setIsViewCreatorOpen(false)
			setEditingViewId(null)
			setEditingViewName('')
			prevPageKeyRef.current = config?.pageKey
		}
	}, [config?.pageKey, setEditingViewId, setIsViewCreatorOpen])

	if (isInitializing && !config?.pageKey) {
		return <div className='h-9' />
	}

	const renderActions = () => {
		if (!actions?.length) {
			return null
		}

		return (
			<div className='flex items-center gap-1 sm:gap-2 shrink-0' data-clears-selection='true'>
				{actions.map(action => {
					// Type discrimination: render custom component or button
					if (action.type === 'component') {
						const componentNode = <div key={action.id}>{action.component}</div>
						return action.access ? (
							<AccessControlled key={action.id} {...action.access}>
								{componentNode}
							</AccessControlled>
						) : (
							componentNode
						)
					}

					// Button action (type is undefined or 'button')
					return <PageHeaderButtonAction key={action.id} action={action} isMobile={isMobile} />
				})}
			</div>
		)
	}

	const getDefaultTabsCount = () =>
		config?.overflowTabs !== undefined
			? Math.min(config.overflowTabs.length, MAX_VISIBLE_TABS)
			: Math.min(tabsToDisplay.length, MAX_VISIBLE_TABS)

	const renderDynamicTabs = () => {
		if (!dynamicTabs) {
			return null
		}

		const { visibleTabs, allTabs, hideTabsSeparator } = dynamicTabs

		// Limit visible tabs to MAX_VISIBLE_TABS (All + 2 others)
		const actualOverflowCount = Math.max(0, visibleTabs.length - MAX_VISIBLE_TABS)

		// Calculate if we need to show custom views dropdown
		const hasCustomViews = customViews.length > 0
		const hasOverflowTabs = allTabs && allTabs.length > 0 && actualOverflowCount > 0
		const shouldShowCustomViewsDropdown = hasCustomViews || hasOverflowTabs
		const showViewCreationButton = ENABLE_VIEW_CREATION && Boolean(config)

		// Handlers for custom views in dynamic tabs context
		const handleDynamicTabsViewSelect = (viewId: string | number) => {
			if (!config) {
				return
			}
			const viewFilters = applyView(String(viewId))
			config.onCustomViewFiltersChange(viewFilters)
		}

		const handleDynamicTabsViewDelete = (viewId: string | number) => {
			const wasActive = String(activeViewId) === String(viewId)
			deleteView(String(viewId))

			if (wasActive && allTabs && allTabs.length > 0) {
				// Clear filters for the deleted view
				config?.onCustomViewFiltersChange([])
				// Shift focus to the first tab (usually "All")
				allTabs[0].onClick?.()
			}
		}

		const handleDynamicTabsClearView = () => {
			if (!config) {
				return
			}
			clearActiveView()
			config.onCustomViewFiltersChange([])
		}

		const handleDynamicTabsReorderViews = (newOrder: typeof customViews) => {
			reorderViews(newOrder)
		}

		const handleDynamicTabsOverflowTabClick = (tab: { key: string; onClick?: () => void }) => {
			if (activeViewId) {
				clearActiveView()
				config?.onCustomViewFiltersChange([])
			}
			clearRegularFilters()
			clearFilters()
			tab.onClick?.()
		}

		const handleDynamicTabsReorderOverflowTabs = (
			reorderedTabs: Array<{ name: string; count: number; key: string; onClick?: () => void; isActive: boolean }>,
		) => {
			if (dynamicTabs?.onTabReorder && reorderedTabs) {
				// Pass the full reordered list - 'all' tab is always first (excluded from dropdown)
				const reorderedKeys = reorderedTabs.map(tab => tab.key)
				// Include 'all' at the beginning since it's not in the reorderable dropdown
				dynamicTabs.onTabReorder(['all', ...reorderedKeys])
			}
		}

		// Only include tabs in the dropdown when there is actual overflow.
		// When all tabs fit in the header (actualOverflowCount === 0), the dropdown
		// should only show custom views — visible navigation tabs must not appear again.
		const dropdownAllTabs =
			actualOverflowCount > 0
				? (allTabs || [])
						.filter(tab => tab.key !== 'all')
						.map(tab => ({
							...tab,
							count: tab.count ?? 0,
							isActive: tab.isActive ?? false,
						}))
				: []

		// Determine dropdown label - show count of items not visible (overflow tabs + custom views)
		// actualOverflowCount is the number of tabs beyond the visible 3
		const dropdownItemCount = actualOverflowCount + customViews.length
		let dynamicDropdownLabel = `${dropdownItemCount} more`
		let dynamicDropdownCount: number | null = null
		const activeOverflowTab = dropdownAllTabs.find(tab => tab.isActive)

		// 1. Calculate base visibility flags using original visibleTabs slice
		const baseVisibleTabs = visibleTabs.slice(0, MAX_VISIBLE_TABS)
		const activeViewInVisible = baseVisibleTabs.some(tab => String(tab.key) === String(activeViewId))
		const activeOverflowTabInVisible = baseVisibleTabs.some(tab => tab.isActive)

		// 2. Map displayedTabs with focus override
		const isSomethingInDropdownActive =
			(activeViewId && !activeViewInVisible) || (activeOverflowTab && !activeOverflowTabInVisible)
		const displayedTabs = baseVisibleTabs.map(tab => ({
			...tab,
			isActive: isSomethingInDropdownActive ? false : tab.isActive,
		}))

		if (activeViewId && !activeViewInVisible) {
			const activeView = customViews.find(view => view.id === activeViewId)
			if (activeView) {
				dynamicDropdownLabel = activeView.name
				dynamicDropdownCount = config?.data
					? getFilteredLengthForView(config.data, activeView.filters as FilterOption[])
					: 0
			}
		} else if (activeOverflowTab && !activeOverflowTabInVisible) {
			dynamicDropdownLabel = activeOverflowTab.name
			dynamicDropdownCount = getTabCountForDropdown(activeOverflowTab)
		} else if (actualOverflowCount > 0 && customViews.length > 0) {
			dynamicDropdownLabel = `${dropdownItemCount} more`
		} else if (customViews.length > 0 && actualOverflowCount === 0) {
			dynamicDropdownLabel = `${customViews.length} more`
		} else if (actualOverflowCount > 0) {
			dynamicDropdownLabel = `${actualOverflowCount} more`
		}

		return (
			<div ref={dynamicTabsContainerRef} className='flex items-center gap-1 relative z-10'>
				{/* Sliding indicator for dynamic tabs - only show when there are visible tabs */}
				{displayedTabs.length > 0 && (
					<div
						className='absolute top-0 h-control bg-header-tab-active-bg rounded-[6px] transition-all duration-300 ease-in-out pointer-events-none z-0'
						style={{
							left: `${dynamicTabsIndicatorStyle.left}px`,
							width: `${dynamicTabsIndicatorStyle.width}px`,
							opacity: dynamicTabsIndicatorStyle.width === 0 ? 0 : 1,
							zIndex: 0,
						}}
					/>
				)}

				<div className='flex items-center gap-1 relative z-10'>
					{displayedTabs.map((tab, index) => (
						<div
							key={tab.key}
							ref={el => {
								dynamicTabsRefs.current[index] = el
							}}>
							<TabButton
								tab={{
									...tab,
									onClick: () => {
										clearRegularFilters()
										clearFilters()
										if (activeViewId) {
											clearActiveView()
											config?.onCustomViewFiltersChange([])
										}
										tab.onClick?.()
									},
								}}
							/>
						</div>
					))}

					{!hideTabsSeparator &&
						displayedTabs.length > 0 &&
						(shouldShowCustomViewsDropdown || showViewCreationButton) && (
							<Separator
								orientation='vertical'
								className='h-(--icon-standalone)! mx-2'
								data-testid='page-header-tabs-separator'
							/>
						)}

					{/* Combined all tabs + custom views dropdown */}
					{shouldShowCustomViewsDropdown && (
						<CustomViewsDropdown
							views={customViews}
							activeViewId={activeViewId}
							onSelectView={handleDynamicTabsViewSelect}
							onDeleteView={handleDynamicTabsViewDelete}
							onClearView={handleDynamicTabsClearView}
							data={config?.data || []}
							onReorderViews={handleDynamicTabsReorderViews}
							dropdownLabel={dynamicDropdownLabel}
							dropdownCount={dynamicDropdownCount}
							dropdownRef={el => {
								// Set both the dropdownRef and the specific index in dynamicTabsRefs
								if (dropdownRef) {
									dropdownRef.current = el
								}
								dynamicTabsRefs.current[displayedTabs.length] = el
							}}
							totalTabsCount={(allTabs?.length || 0) + customViews.length}
							overflowActionTabs={dropdownAllTabs}
							onOverflowTabClick={handleDynamicTabsOverflowTabClick}
							onReorderOverflowTabs={handleDynamicTabsReorderOverflowTabs}
						/>
					)}

					{/* Plus Button to Create View — disabled via ENABLE_VIEW_CREATION */}
					{showViewCreationButton && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant='ghost'
									size='icon'
									className='size-(--height-control) p-0 border-none rounded-[6px]'
									onClick={() => {
										// Clear active view and filters when creating a new view
										if (!isViewCreatorOpen) {
											clearActiveView()
											config?.onCustomViewFiltersChange([])
											clearRegularFilters()
											// Clear current context filters as well
											setCurrentFilters([])
										}
										setIsViewCreatorOpen(!isViewCreatorOpen)
									}}>
									<PlusIcon className='size-(--icon-standalone)' />
								</Button>
							</TooltipTrigger>
							<TooltipContent side='bottom'>
								<p>Create custom view</p>
							</TooltipContent>
						</Tooltip>
					)}
				</div>
			</div>
		)
	}

	// Helper function to determine which tabs to show
	const getVisibleAndOverflowTabs = (customViews: CustomView[]) => {
		// overflowTabs pages use action tabs first, router-tab pages use router tabs.
		const defaultTabsCount = getDefaultTabsCount()
		const additionalTabsNeeded = Math.max(0, MAX_VISIBLE_TABS - defaultTabsCount)

		// Take first N custom views to show as separate tabs
		const visibleCustomViews = customViews.slice(0, additionalTabsNeeded)

		// All custom views go in dropdown
		const dropdownViews = customViews

		return {
			visibleCustomViews,
			dropdownViews,
			shouldShowMore: customViews.length > additionalTabsNeeded,
		}
	}

	// Split actions into left-aligned "title" components and regular right-side actions
	const titleActions = rawActions?.filter(action => action.type === 'component' && action.id.endsWith('-title')) ?? []
	const actions = rawActions?.filter(action => !(action.type === 'component' && action.id.endsWith('-title'))) ?? []

	if (!tabsToDisplay?.length && !rawActions?.length && !dynamicTabs && !backButton) {
		return null
	}

	const handleSelectView = (viewId: string | number) => {
		if (!config) {
			return
		}

		// Clear regular filters when selecting a custom view
		clearRegularFilters()

		const viewFilters = applyView(String(viewId))
		config.onCustomViewFiltersChange(viewFilters)

		// Find view position to determine correct activeIndex.
		const defaultTabsCount = getDefaultTabsCount()
		const { visibleCustomViews } = getVisibleAndOverflowTabs(customViews)
		const visibleViewIndex = visibleCustomViews.findIndex(v => v.id === viewId)

		if (visibleViewIndex !== -1) {
			// View is visible, set its index
			const viewIndex = defaultTabsCount + visibleViewIndex
			setActiveIndex(viewIndex)
		} else {
			// View is in dropdown, set to dropdown button's index
			const dropdownIndex = defaultTabsCount + visibleCustomViews.length
			if (dropdownRef.current) {
				tabRefs.current[dropdownIndex] = dropdownRef.current as unknown as HTMLElement
			}
			setActiveIndex(dropdownIndex)
		}
	}

	//  Handle view deletion
	const handleDeleteView = (viewId: string | number) => {
		const isDeletingActiveView = String(viewId) === String(activeViewId)
		deleteView(String(viewId))
		toast.success('Custom view deleted')

		if (isDeletingActiveView && config) {
			config.onCustomViewFiltersChange([])
			// Reset to first tab; trigger "All" onClick for overflowTabs pages.
			config.overflowTabs?.[0]?.onClick?.()
			setActiveIndex(0)
		}
	}

	/* 
	const handleEditView = (view: { id: string | number; name: string; filters: FilterOption[] }) => {
		// Use flushSync to ensure state updates are synchronous
		// This prevents race conditions where editingViewId isn't set yet when filters are applied
		flushSync(() => {
			setEditingViewId(String(view.id))
			setEditingViewName(view.name)
			setCurrentFilters(view.filters)
		})

		// Apply the view's filters to the search bar so they can be edited
		if (config) {
			config.onCustomViewFiltersChange(view.filters)
		}

		setIsViewCreatorOpen(true)
	}
	*/

	// Handle clearing active view
	const handleClearView = () => {
		if (!config) {
			return
		}
		clearActiveView()
		config.onCustomViewFiltersChange([])
		// Reset to first tab; trigger "All" onClick for overflowTabs pages.
		config.overflowTabs?.[0]?.onClick?.()
		setActiveIndex(0)
	}

	//  Handle creating new view
	const handleCreateView = (name: string, filters: FilterOption[]) => {
		if (!config) {
			return
		}

		if (editingViewId) {
			const filtersToSave = currentFilters
			updateView(editingViewId, { name, filters: filtersToSave })
			config.onCustomViewFiltersChange(filtersToSave)
			toast.success(`Custom view "${name}" updated successfully`)
			setEditingViewId(null)
			setEditingViewName('')
			return
		}

		// Create the view
		createView(name, filters)
		config.onCustomViewFiltersChange(filters)
		clearRegularFilters()
		clearFilters()
		toast.success(`Custom view "${name}" created successfully`)

		// Calculate activeIndex for the new view based on tab slot layout.
		const defaultTabsCount = getDefaultTabsCount()
		const availableSlotsForCustomViews = Math.max(0, MAX_VISIBLE_TABS - defaultTabsCount)
		const remainingSlotsAfterOverflow = availableSlotsForCustomViews

		// The new view will be at the end of customViews array
		const newViewIndexInCustomViews = customViews.length

		if (newViewIndexInCustomViews < remainingSlotsAfterOverflow) {
			// New view will be visible as a tab.
			const newViewTabIndex = defaultTabsCount + newViewIndexInCustomViews
			setActiveIndex(newViewTabIndex)
		} else {
			// New view will be in the dropdown.
			const dropdownIndex = defaultTabsCount + remainingSlotsAfterOverflow
			if (dropdownRef.current) {
				tabRefs.current[dropdownIndex] = dropdownRef.current as unknown as HTMLElement
			}
			setActiveIndex(dropdownIndex)
		}
	}

	// Handle tab click - clear custom view and filters when clicking tabs
	const handleTabClickWithClear = (index: number, onClick?: () => void) => {
		// If coming from a dropdown view (activeIndex === -1), use dropdown position
		if (activeIndex === -1 && dropdownRef.current && containerRef.current) {
			const containerRect = containerRef.current.getBoundingClientRect()
			const dropdownRect = dropdownRef.current.getBoundingClientRect()

			// Temporarily set indicator to dropdown position
			setIndicatorStyle({
				left: dropdownRect.left - containerRect.left,
				width: dropdownRect.width,
			})
		}

		// Call original tab click handler FIRST
		prevIndexRef.current = activeIndex
		setActiveIndex(index)
		onClick?.()

		// Clear search bar filters and device filter store
		clearRegularFilters()
		clearFilters()

		// THEN clear custom view immediately
		if (config && activeViewId) {
			clearActiveView()
			config.onCustomViewFiltersChange([])
		}
	}

	const renderCustomViewsSection = () => {
		if (!config || hideCustomViews) {
			return null
		}

		if (config.overflowTabs !== undefined && config.overflowTabs.length === 0) {
			// If overflowTabs is defined but empty, page wants to hide all tabs (e.g., edit panel view)
			return null
		}

		const overflowRouterTabs = usingOverflowTabs ? [] : tabsToDisplay.slice(MAX_VISIBLE_TABS)

		// Convert overflow router tabs to dropdown format
		const routerTabsForDropdown = overflowRouterTabs.map(item => {
			const pathParts = item.path.split('/')
			const lastPart = pathParts[pathParts.length - 1]
			const tabCount = isAccessRestricted ? 0 : tabCounts[lastPart] || 0
			const { linkTo, linkParams, hasParams } = getRouteInfo(normalizedPath, tenantId, lastPart, orgId)

			// Find the original index in tabsToDisplay for proper activeIndex handling
			const originalIndex = tabsToDisplay.findIndex(tab => tab.id === item.id)
			const isActive = !activeViewId && logicalActiveRouterIndex === originalIndex

			return {
				name: item.title,
				count: tabCount,
				key: item.id,
				isActive,
				onClick: () => {
					clearRegularFilters()
					clearFilters()
					if (config && activeViewId) {
						clearActiveView()
						config.onCustomViewFiltersChange([])
					}
					// Navigate to the tab
					if (hasParams) {
						navigate({ to: linkTo, params: linkParams, search: tabSearch })
					} else {
						const routePath = isOrgLevel
							? `/orgs/${orgId}${item.path}`
							: `/orgs/${orgId}/projects/${tenantId}${item.path}`
						navigate({ to: routePath, search: tabSearch })
					}
				},
			}
		})

		// Combine router overflow tabs with action overflow tabs for dropdown.
		// Action tabs are only included when some of them don't fit as visible tabs;
		// the dropdown then lists ALL of them (not just hidden ones) so drag-reorder
		// can produce a complete order, but the label only counts the hidden ones.
		const hasActionTabOverflow = overflowActionTabs.length > visibleOverflowTabs.length
		const dropdownOverflowTabs = [
			...routerTabsForDropdown,
			...(hasActionTabOverflow ? overflowActionTabs.filter(tab => tab.key !== 'all') : []),
		]
		const hiddenOverflowTabsCount =
			routerTabsForDropdown.length + Math.max(0, overflowActionTabs.length - visibleOverflowTabs.length)
		// Only custom views that didn't fit as visible tabs go in the dropdown
		const dropdownCustomViews = customViews.slice(visibleCustomViews.length)

		const totalAllTabs = (usingOverflowTabs ? 0 : tabsToDisplay.length) + overflowActionTabs.length + customViews.length
		// Show dropdown only when there are items that don't fit as visible tabs
		const shouldShowDropdown = dropdownCustomViews.length > 0 || dropdownOverflowTabs.length > 0

		const isDropdownActive = isSomethingInDropdownActive && activeIndex === dropdownIndex

		// The separator only earns its place when tabs sit on its left and views/dropdown on its right
		const hasContentBeforeSeparator = shouldShowRouterTabs || visibleOverflowTabs.length > 0
		const hasContentAfterSeparator = visibleCustomViews.length > 0 || shouldShowDropdown || ENABLE_VIEW_CREATION

		// Determine dropdown label and count
		const remainingCount = totalAllTabs - MAX_VISIBLE_TABS
		let dropdownLabel = `${remainingCount} more`
		let dropdownCount: number | null = null

		const activeOverflowTabForLabel = dropdownOverflowTabs.find(tab => tab.isActive)

		if (activeViewId && !activeViewInVisible) {
			const activeView = customViews.find(view => view.id === activeViewId)
			if (activeView) {
				dropdownLabel = activeView.name
				dropdownCount = config.data ? getFilteredLengthForView(config.data, activeView.filters) : 0
			}
		} else if (activeOverflowTabForLabel && !isActiveOverflowTabInVisible) {
			dropdownLabel = activeOverflowTabForLabel.name
			dropdownCount = getTabCountForDropdown(activeOverflowTabForLabel)
		} else if (dropdownOverflowTabs.length > 0 && dropdownCustomViews.length === 0) {
			dropdownLabel = `${hiddenOverflowTabsCount} more`
		} else if (dropdownOverflowTabs.length > 0 && dropdownCustomViews.length > 0) {
			dropdownLabel = `${hiddenOverflowTabsCount + dropdownCustomViews.length} more`
		} else if (dropdownCustomViews.length > 0) {
			dropdownLabel = `${dropdownCustomViews.length} more`
		}

		const handleSafeReorder = (newOrder: typeof customViews) => {
			reorderViews(newOrder)
		}

		const handleOverflowTabsReorder = (reorderedTabs: typeof overflowActionTabs) => {
			const reorderHandler = dynamicTabs?.onTabReorder || config?.onTabReorder

			if (reorderHandler) {
				// Include 'all' tab at the beginning (it's always first and not in the reorderable list)
				const allTab = overflowActionTabs.find(tab => tab.key === 'all')
				const reorderedKeys = reorderedTabs.map(tab => tab.key)
				const fullOrder = allTab ? ['all', ...reorderedKeys] : reorderedKeys
				reorderHandler(fullOrder)
			}
		}

		const handleOverflowTabClick = (tab: (typeof overflowActionTabs)[0], overflowIndex = 0) => {
			// Clear custom view first
			if (activeViewId) {
				clearActiveView()
				config.onCustomViewFiltersChange([])
			}
			// Move indicator to this overflow tab's ref position
			setActiveIndex(overflowIndex)

			// Then execute tab's onClick
			clearRegularFilters()
			clearFilters()
			tab.onClick?.()
		}

		return (
			<div className='flex items-center gap-1.5 size-full'>
				{/* Show visible overflow tabs FIRST — register refs so the indicator can land on them */}
				{visibleOverflowTabs.map((tab, overflowIndex) => {
					const setOverflowTabRef = (el: HTMLElement | null) => {
						tabRefs.current[overflowIndex] = el
					}
					return (
						<Button
							key={tab.key}
							ref={setOverflowTabRef}
							variant='ghost'
							size='sm'
							className={cn(
								'h-control border-none text-body hover:text-foreground relative z-10 transition-colors',
								tab.isActive && !isSomethingInDropdownActive
									? 'text-header-tab-active-text hover:bg-header-tab-active-bg-hover'
									: 'bg-transparent text-header-tab-inactive-text',
							)}
							onClick={() => handleOverflowTabClick(tab, overflowIndex)}>
							<span>{tab.name}</span>
							<TabCountBadge
								count={tab.count}
								active={tab.isActive && !isSomethingInDropdownActive}
								showCount={shouldShowTabCountBadge(tab)}
							/>
						</Button>
					)
				})}

				{hasContentBeforeSeparator && hasContentAfterSeparator && (
					<Separator
						orientation='vertical'
						className='h-(--icon-standalone)! mx-2'
						data-testid='page-header-views-separator'
					/>
				)}

				{/* Show visible custom views as separate tabs */}
				<CustomViewTabs
					visibleCustomViews={visibleCustomViews}
					tabsToDisplay={tabsToDisplay}
					activeViewId={isSomethingInDropdownActive ? '' : (activeViewId ?? '')}
					handleSelectView={handleSelectView}
					// handleEditView={handleEditView}
					handleDeleteView={handleDeleteView}
					handleClearView={handleClearView}
					tabRefs={tabRefs}
					setActiveIndex={setActiveIndex}
					config={config}
					baseIndex={effectiveBaseIndex}
				/>

				{/* Dropdown for ALL items */}
				{shouldShowDropdown && (
					<CustomViewsDropdown
						views={dropdownCustomViews}
						activeViewId={activeViewId}
						onSelectView={handleSelectView}
						onDeleteView={handleDeleteView}
						onClearView={handleClearView}
						data={config.data}
						onReorderViews={handleSafeReorder}
						dropdownLabel={dropdownLabel}
						dropdownCount={isDropdownActive ? dropdownCount : null}
						dropdownRef={el => {
							if (dropdownRef) {
								dropdownRef.current = el
							}
							tabRefs.current[dropdownIndex] = el
						}}
						totalTabsCount={totalAllTabs}
						overflowActionTabs={dropdownOverflowTabs}
						onOverflowTabClick={handleOverflowTabClick}
						onReorderOverflowTabs={handleOverflowTabsReorder}
					/>
				)}

				{/* Plus Button to Create View — disabled via ENABLE_VIEW_CREATION */}
				{ENABLE_VIEW_CREATION && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant='ghost'
								size='icon'
								className='size-control p-0 border-none rounded-[6px]'
								onClick={() => {
									// Clear active view and filters when creating a new view
									if (!isViewCreatorOpen) {
										clearActiveView()
										config?.onCustomViewFiltersChange([])
										clearRegularFilters()
										// Clear current context filters as well
										setCurrentFilters([])
									}
									setIsViewCreatorOpen(!isViewCreatorOpen)
								}}>
								<PlusIcon className='size-(--icon-standalone)' />
							</Button>
						</TooltipTrigger>
						<TooltipContent side='bottom'>
							<p>Create custom view</p>
						</TooltipContent>
					</Tooltip>
				)}
			</div>
		)
	}

	// Check if we should show any tabs at all
	// Logic matches internal checks in renderCustomViewsSection and the Router Tabs render block
	const shouldShowRouterTabs = tabsToDisplay?.length > 0 && config?.overflowTabs === undefined
	const shouldShowCustomViews =
		config && !hideCustomViews && !(config.overflowTabs !== undefined && config.overflowTabs.length === 0)
	const isAnyTabVisible = shouldShowRouterTabs || shouldShowCustomViews

	return (
		<div className='flex items-center justify-between w-full gap-2'>
			{isViewCreatorOpen && config ? (
				<CustomViewCreator
					isOpen={isViewCreatorOpen}
					onClose={() => {
						setIsViewCreatorOpen(false)
						setEditingViewId(null)
						setEditingViewName('')
					}}
					onCancel={() => {
						if (activeViewId) {
							const viewFilters = applyView(activeViewId)
							config.onCustomViewFiltersChange(viewFilters || [])
						} else {
							config.onCustomViewFiltersChange([])
						}
					}}
					columns={config.columns}
					data={config.data}
					onSave={handleCreateView}
					initialFilters={
						editingViewId ? customViews.find(v => v.id === editingViewId)?.filters || currentFilters : currentFilters
					}
					editingViewId={editingViewId}
					editingViewName={editingViewName}
					existingViewNames={customViews.map(view => view.name)}
				/>
			) : (
				<>
					{/* Left side - Navigation Tabs + Back + Title */}
					{dynamicTabs ? (
						<div className='flex gap-1 min-w-0 flex-1 items-center' data-clears-selection='true'>
							<HeaderBackAndTitleControls backButton={backButton} titleActions={titleActions} />
							{renderDynamicTabs()}
						</div>
					) : (
						<>
							<HeaderBackAndTitleControls
								backButton={backButton}
								titleActions={titleActions}
								backButtonClassName='relative z-10'
							/>
							<div ref={containerRef} className='flex gap-1 min-w-0 flex-1 relative' data-clears-selection='true'>
								{/* Sliding indicator - provides unified active state for all tabs */}
								{/* Only show router tabs indicator when not using dynamic tabs AND tabs are visible */}
								{!dynamicTabs && isAnyTabVisible && (
									<div
										className='absolute top-0 h-control bg-header-tab-active-bg rounded-[6px] transition-all duration-300 ease-in-out pointer-events-none z-0'
										style={{
											left: `${indicatorStyle.left}px`,
											width: `${indicatorStyle.width}px`,
											opacity: indicatorStyle.width === 0 ? 0 : 1,
											zIndex: 0,
										}}
									/>
								)}

								<div className='flex relative z-10'>
									{/*
							Only show router navigation tabs when:
							1. Custom views are NOT using overflowTabs (including empty array)

							Why this check is needed:
							- Router tabs come from the navigation structure (nav-data.tsx) and represent child routes
							- overflowTabs are action tabs passed explicitly by pages via useRegisterCustomViews
							- Some pages (eg: DashboardDisplayPage) use overflowTabs to replace router tabs entirely
							- This prevents duplicate/conflicting tabs from appearing together
							- When overflowTabs is an empty array, the page wants to hide all tabs (e.g., edit panel view)

							How pages use tabs:
							1. Pages with router child routes (e.g., SessionRulesPage with /all, /no-signal, /signal-based):
								- Don't use overflowTabs
								- Router tabs render normally here
								- Custom views appear alongside router tabs

							2. Pages using overflowTabs (eg: DashboardDisplayPage):
								- Pass action tabs via overflowTabs parameter in useRegisterCustomViews
								- Router tabs are hidden (this condition)
								- Only overflowTabs + custom views are shown

							3. Pages that could theoretically need both:
								- Simply don't use overflowTabs
								- Router tabs + custom views render together normally

							4. Pages that want to hide all tabs (e.g., edit panel view):
								- Pass empty array [] for overflowTabs
								- All tabs are hidden
						*/}
									{shouldShowRouterTabs &&
										(() => {
											// Limit router tabs to MAX_VISIBLE_TABS
											const visibleRouterTabs = tabsToDisplay.slice(0, MAX_VISIBLE_TABS)
											return (
												<div className='flex items-center gap-1.5 border-b-0 relative z-10'>
													{visibleRouterTabs.map((item, index) => {
														const pathParts = item.path.split('/')
														const lastPart = pathParts[pathParts.length - 1]
														const tabCount = isAccessRestricted ? 0 : tabCounts[lastPart]
														const { linkTo, linkParams, hasParams } = getRouteInfo(
															normalizedPath,
															tenantId,
															lastPart,
															orgId,
														)
														const isActive = !activeViewId && activeIndex === index

														const setTabRef = (el: HTMLElement | null) => {
															tabRefs.current[index] = el
														}

														if (!hasParams) {
															// Require orgId for all routes, and tenantId for project routes
															if (!orgId) {
																return null
															}
															const routePath = isOrgLevel
																? `/orgs/${orgId}${item.path}`
																: `/orgs/${orgId}/projects/${tenantId}${item.path}`
															return (
																<RouterTab
																	key={item.id}
																	// @ts-expect-error - Dynamic route path
																	to={routePath}
																	search={tabSearch}
																	onClick={() => handleTabClickWithClear(index)}
																	active={isActive}
																	count={tabCount}
																	tabRef={setTabRef}>
																	{item.title}
																</RouterTab>
															)
														}

														return (
															<RouterTab
																key={item.id}
																// @ts-expect-error - Dynamic route path
																to={linkTo}
																params={linkParams}
																search={tabSearch}
																active={isActive}
																onClick={() => handleTabClickWithClear(index)}
																count={tabCount}
																tabRef={setTabRef}>
																{item.title}
															</RouterTab>
														)
													})}
												</div>
											)
										})()}
									{/* Custom Views Section - now part of the same animated container */}
									<div className='relative z-10'>{renderCustomViewsSection()}</div>
								</div>
							</div>
						</>
					)}
					{/*
						Right side - page controls, then Action Buttons.

						The slot is for a page with no CTAs that would rather put its own controls here (device
						management does; see `usePageHeaderControls`). It stays ahead of the actions so a page
						that has both keeps its primary CTA rightmost.

						`empty:hidden` is what keeps every other page byte-identical: with nothing portalled in
						the slot is `display:none`, so the row's `gap-2` never allocates space for it. And it
						deliberately carries no `data-clears-selection` — these controls live in the page body
						today, where interacting with them leaves the table selection alone.
					*/}
					<div
						ref={setHeaderControlsSlot}
						className='flex shrink-0 items-center gap-2 empty:hidden'
						data-testid='page-header-controls'
					/>
					{renderActions()}
				</>
			)}
		</div>
	)
}
